import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';
import { FullPregnancyTable } from './_pregnancyHelpers';

const fmt = d => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

export default function DueDateCalc() {
  const [method, setMethod] = useState('lmp');
  const [lmp, setLmp] = useState('2026-01-01');
  const [cycle, setCycle] = useState(28);
  const [conception, setConception] = useState('2026-01-15');
  const [usDate, setUsDate] = useState('2026-03-01');
  const [usWeeks, setUsWeeks] = useState(8);
  const [usDays, setUsDays] = useState(0);
  const clear = () => { setMethod('lmp'); setLmp('2026-01-01'); setCycle(28); setConception('2026-01-15'); setUsDate('2026-03-01'); setUsWeeks(8); setUsDays(0); };

  const c = useMemo(() => {
    const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
    let dueDate, concDate, currWeeks, currDays;
    const today = new Date();
    if (method === 'lmp') {
      const lmpD = new Date(lmp); const offset = cycle - 28;
      dueDate = addDays(lmpD, 280 + offset);
      concDate = addDays(lmpD, 14 + offset);
      const gAge = Math.floor((today - lmpD) / 86400000);
      currWeeks = Math.floor(gAge/7); currDays = gAge % 7;
    } else if (method === 'conception') {
      const cD = new Date(conception);
      dueDate = addDays(cD, 266); concDate = cD;
      const gAge = Math.floor((today - cD) / 86400000) + 14;
      currWeeks = Math.floor(gAge/7); currDays = gAge % 7;
    } else {
      const usD = new Date(usDate);
      const gAgeDays = usWeeks*7 + usDays;
      dueDate = addDays(usD, 280 - gAgeDays);
      concDate = addDays(dueDate, -266);
      const gAgeToday = gAgeDays + Math.floor((today - usD) / 86400000);
      currWeeks = Math.floor(gAgeToday/7); currDays = gAgeToday % 7;
    }
    return { dueDate, concDate, currWeeks: Math.max(0,currWeeks), currDays: Math.max(0,currDays) };
  }, [method, lmp, cycle, conception, usDate, usWeeks, usDays]);

  return (
    <CalcShell look="cnet" title="Pregnancy Due Date Calculator" subtitle="Estimate your baby's due date from your LMP, conception date, or ultrasound.">
      <CalcNetPanel slug="dd" tabs={['Due Date']} activeTab={0} onTabChange={()=>{}} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Estimated Due Date</div>
          <div className="text-xl font-bold text-[#2f9c3f]" data-testid="dd-result">{fmt(c.dueDate)}</div>
          <ResultTable testid="dd-detail-table" rows={[
            ['Conception date', fmt(c.concDate)],
            ['Gestational age today', <span className="font-bold">{c.currWeeks} weeks {c.currDays} days</span>],
            ['End of 1st trimester (12 wk)', fmt(new Date(c.dueDate.getTime() - (40 - 12) * 7 * 86400000))],
            ['End of 2nd trimester (26 wk)', fmt(new Date(c.dueDate.getTime() - (40 - 26) * 7 * 86400000))],
          ]}/>
          {/* Week-by-week milestone table */}
          <div className="mt-4">
            <h4 className="font-bold text-slate-900 mb-2 text-sm">Week-by-week Milestones</h4>
            <table className="w-full text-xs border border-slate-300" data-testid="dd-weekly-table">
              <thead className="bg-[#1e40af] text-white"><tr><th className="px-2 py-1.5 text-left">Week</th><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Milestone</th></tr></thead>
              <tbody>
                {[
                  [4, "Positive pregnancy test possible"],
                  [8, "First prenatal visit typical"],
                  [12, "End of first trimester · low miscarriage risk"],
                  [16, "Second-trimester screening · Quad-screen window"],
                  [20, "Anatomy ultrasound · gender reveal possible"],
                  [24, "Viability threshold"],
                  [28, "Start of third trimester · glucose test window"],
                  [32, "Baby's movements increase · rapid growth"],
                  [36, "Baby considered 'early term' next week"],
                  [37, "Full-term begins"],
                  [40, "Due date · full term"],
                ].map(([w, note], i) => {
                  const dt = new Date(c.dueDate.getTime() - (40 - w) * 7 * 86400000);
                  const isPast = dt < new Date();
                  return (
                    <tr key={w} className={`${i % 2 ? 'bg-slate-50' : ''} ${w === c.currWeeks ? 'bg-emerald-100 font-bold' : ''}`}>
                      <td className="px-2 py-1">Week {w}</td>
                      <td className="px-2 py-1 font-mono">{fmt(dt)}</td>
                      <td className={`px-2 py-1 ${isPast ? 'text-slate-500' : 'text-slate-900'}`}>{note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <FullPregnancyTable dueDate={c.dueDate} currentWeek={c.currWeeks} testId="dd-full-table" />
        </div>
      }>
        <Row label="Calculate By">
          <select value={method} onChange={e => setMethod(e.target.value)} data-testid="dd-method" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            <option value="lmp">Last Menstrual Period</option>
            <option value="conception">Conception Date</option>
            <option value="ultrasound">Ultrasound</option>
          </select>
        </Row>
        {method === 'lmp' && <>
          <Row label="LMP Date"><input type="date" value={lmp} onChange={e => setLmp(e.target.value)} data-testid="dd-lmp" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm"/></Row>
          <Row label="Cycle length"><UnitInput value={cycle} onChange={setCycle} unit="days" testid="dd-cycle"/></Row>
        </>}
        {method === 'conception' && (
          <Row label="Conception Date"><input type="date" value={conception} onChange={e => setConception(e.target.value)} data-testid="dd-conc" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm"/></Row>
        )}
        {method === 'ultrasound' && <>
          <Row label="Ultrasound Date"><input type="date" value={usDate} onChange={e => setUsDate(e.target.value)} data-testid="dd-us" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm"/></Row>
          <Row label="Gestational Age">
            <UnitInput value={usWeeks} onChange={setUsWeeks} unit="weeks" testid="dd-usw"/>
            <UnitInput value={usDays} onChange={setUsDays} unit="days" testid="dd-usd"/>
          </Row>
        </>}
      </CalcNetPanel>
    </CalcShell>
  );
}
